/**
 * Client-side: resolve a valid hospital org when profile organizationId(s) are missing
 * or point to non-existent `organizations/{id}` docs. Persists the fix to users/{uid}
 * when rules allow (self-update).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const ORG_FETCH_LIMIT = 100;

/** Firestore users.organizationId must be a document id string; heal legacy object values. */
export function normalizeOrganizationId(value) {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'object') {
    const id = value.id ?? value.organizationId ?? value.uid;
    if (typeof id === 'string' && id.trim() !== '') return id.trim();
  }
  return null;
}

export function normalizeOrganizationIdsFromUser(user) {
  if (!user || typeof user !== 'object') return [];
  if (Array.isArray(user.organizationIds) && user.organizationIds.length > 0) {
    return [...new Set(user.organizationIds.map(normalizeOrganizationId).filter(Boolean))];
  }
  const single = normalizeOrganizationId(user.organizationId ?? user.organization_id);
  return single ? [single] : [];
}

export async function fetchExistingOrganizationDocs() {
  const snap = await getDocs(
    query(collection(db, 'organizations'), limit(ORG_FETCH_LIMIT))
  );
  return snap.docs.map((d) => ({ orgId: d.id, snap: d }));
}

export function pickUserDepartmentId(user) {
  if (!user || typeof user !== 'object') return null;
  const keys = [
    'primary_department_id',
    'primaryDepartmentId',
    'department_id',
    'departmentId',
    'primary_department'
  ];
  for (const k of keys) {
    const v = user[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

/**
 * Walk department → floor → building → location → organizationId (same chain as admin scripts).
 * @returns {Promise<string|null>}
 */
export async function resolveOrganizationIdFromDepartmentId(departmentId) {
  if (!departmentId) return null;
  try {
    const deptSnap = await getDoc(doc(db, 'departments', departmentId));
    if (!deptSnap.exists()) return null;
    const floorId = deptSnap.data().floorId;
    if (!floorId) return null;

    const floorSnap = await getDoc(doc(db, 'floors', floorId));
    if (!floorSnap.exists()) return null;
    const buildingId = floorSnap.data().buildingId;
    if (!buildingId) return null;

    const buildingSnap = await getDoc(doc(db, 'buildings', buildingId));
    if (!buildingSnap.exists()) return null;
    const locationId = buildingSnap.data().locationId;
    if (!locationId) return null;

    const locationSnap = await getDoc(doc(db, 'locations', locationId));
    if (!locationSnap.exists()) return null;
    const orgId = locationSnap.data().organizationId || null;
    return orgId || null;
  } catch {
    return null;
  }
}

async function persistUserOrganization(uid, orgId) {
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, {
    organizationId: orgId,
    organization_id: orgId,
    organizationIds: [orgId],
    primaryOrganizationId: orgId,
    updated_at: new Date().toISOString()
  });
}

/**
 * @param {string[]} profileOrgIds — from organizationIds / organizationId on user profile
 * @param {object} user — profile object with uid + optional department fields
 * @returns {Promise<{ entries: { orgId: string, snap: import('firebase/firestore').DocumentSnapshot }[], persisted: boolean, healReason: string | null }>}
 */
export async function resolveOrgEntriesWithAutoHeal(profileOrgIds, user) {
  const uid = user?.uid;
  const rawLegacy = user?.organizationId ?? user?.organization_id;
  const ids = [
    ...new Set(
      [...(profileOrgIds || []), ...normalizeOrganizationIdsFromUser(user)]
        .map(normalizeOrganizationId)
        .filter(Boolean)
    )
  ];

  if (
    uid &&
    rawLegacy &&
    typeof rawLegacy === 'object' &&
    ids.length === 1
  ) {
    try {
      await persistUserOrganization(uid, ids[0]);
      console.info(
        '[organizationAutoHeal] Repaired organizationId stored as object → string:',
        ids[0]
      );
    } catch (e) {
      console.warn(
        '[organizationAutoHeal] Could not repair org id on profile:',
        e?.message || e
      );
    }
  }

  const snaps = await Promise.all(
    ids.map((orgId) => getDoc(doc(db, 'organizations', orgId)))
  );

  let entries = ids
    .map((orgId, i) => ({ orgId, snap: snaps[i] }))
    .filter(({ snap }) => snap.exists());

  if (entries.length > 0) {
    return { entries, persisted: false, healReason: null };
  }

  const orgDocs = await fetchExistingOrganizationDocs();
  if (orgDocs.length === 0) {
    return { entries: [], persisted: false, healReason: null };
  }

  /** Single tenant / single hospital in project — bind user to the only org. */
  if (orgDocs.length === 1) {
    const only = orgDocs[0];
    if (uid) {
      try {
        await persistUserOrganization(uid, only.orgId);
        console.info(
          '[organizationAutoHeal] Bound user to the only organization in the project:',
          only.orgId
        );
        return {
          entries: [{ orgId: only.orgId, snap: only.snap }],
          persisted: true,
          healReason: 'single_organization_in_project'
        };
      } catch (e) {
        console.warn(
          '[organizationAutoHeal] Could not persist org binding (session still uses resolved org):',
          e?.message || e
        );
        return {
          entries: [{ orgId: only.orgId, snap: only.snap }],
          persisted: false,
          healReason: 'single_organization_in_project_no_persist'
        };
      }
    }
    return {
      entries: [{ orgId: only.orgId, snap: only.snap }],
      persisted: false,
      healReason: null
    };
  }

  /** Multi-org: infer from clinical chain if user has a department. */
  const deptId = pickUserDepartmentId(user);
  const fromDept = deptId
    ? await resolveOrganizationIdFromDepartmentId(deptId)
    : null;

  if (
    fromDept &&
    orgDocs.some((o) => o.orgId === fromDept)
  ) {
    const match = orgDocs.find((o) => o.orgId === fromDept);
    if (uid) {
      try {
        await persistUserOrganization(uid, fromDept);
        console.info(
          '[organizationAutoHeal] Bound user via department chain → organization:',
          fromDept
        );
        return {
          entries: [{ orgId: match.orgId, snap: match.snap }],
          persisted: true,
          healReason: 'department_chain'
        };
      } catch (e) {
        console.warn(
          '[organizationAutoHeal] Could not persist department-based org:',
          e?.message || e
        );
        return {
          entries: [{ orgId: match.orgId, snap: match.snap }],
          persisted: false,
          healReason: 'department_chain_no_persist'
        };
      }
    }
    return {
      entries: [{ orgId: match.orgId, snap: match.snap }],
      persisted: false,
      healReason: null
    };
  }

  if (ids.length > 0) {
    console.warn(
      '[organizationAutoHeal] Profile organization id(s) are invalid and could not be auto-mapped (multiple hospitals, no department chain).',
      ids
    );
  }

  return { entries: [], persisted: false, healReason: null };
}
